import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { PageScrollLayout } from '../components/PageScrollLayout'
import { nextSortDirection, SortableTableHeader, type SortDirection } from '../components/SortableTableHeader'
import { Api, type DatasetEntry } from '../services/api'

export function DatasetsPage() {
  const list = useQuery({ queryKey: ['datasets'], queryFn: Api.listDatasets })
  const [selected, setSelected] = useState<string | null>(null)
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | null>(null)

  const sample = useQuery({
    queryKey: ['datasets', selected, 'sample'],
    queryFn: () => Api.getDataset(selected!, { limit: 50 }),
    enabled: !!selected,
  })

  const sampleRows = useMemo(() => {
    if (!sample.data) return []
    if (!sort) return sample.data.rows
    return [...sample.data.rows].sort((a, b) => compareCellValues(a[sort.key], b[sort.key]) * (sort.direction === 'asc' ? 1 : -1))
  }, [sample.data, sort])

  const onSampleSort = (key: string) => {
    setSort((prev) => {
      if (prev?.key === key) return { key, direction: nextSortDirection(prev.direction) }
      return { key, direction: 'asc' }
    })
  }

  return (
    <PageScrollLayout
      title="Datasets"
      subtitle="Every parquet table in the pipeline. Click one to preview the first 50 rows."
    >
      {list.isLoading && <p className="text-slate-500">Loading…</p>}
      {list.isError && <p className="text-rose-600">Failed to load datasets.</p>}

      {list.data && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {list.data.map((d) => (
            <DatasetCard
              key={d.name}
              dataset={d}
              isSelected={selected === d.name}
              onClick={() => {
                setSelected(d.name)
                setSort(null)
              }}
            />
          ))}
        </div>
      )}

      {selected && (
        <section className="card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Sample: {selected}
            </h2>
            <button type="button" className="btn-secondary" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>
          {sample.isLoading && <p className="text-slate-500">Loading…</p>}
          {sample.isError && <p className="text-rose-600">No data available.</p>}
          {sample.data && (
            <>
              <p className="mb-2 text-xs text-slate-500">
                Showing {sample.data.rows.length} of {sample.data.total.toLocaleString()} rows
              </p>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      {sample.data.columns.map((c) => (
                        <SortableTableHeader
                          key={c}
                          label={c}
                          sortKey={c}
                          activeKey={sort?.key ?? null}
                          direction={sort?.direction ?? 'asc'}
                          onSort={onSampleSort}
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sampleRows.map((row, i) => (
                      <tr key={i}>
                        {sample.data!.columns.map((c) => (
                          <td key={c}>{fmt(row[c])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </PageScrollLayout>
  )
}

function DatasetCard({
  dataset,
  isSelected,
  onClick,
}: {
  dataset: DatasetEntry
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'card text-left transition hover:border-slate-400',
        isSelected ? 'border-slate-900 ring-2 ring-slate-900' : '',
      ].join(' ')}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">{dataset.name}</h3>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs uppercase text-slate-500">
          {dataset.group}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-600">{dataset.description}</p>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-slate-500">Rows</dt>
          <dd className="font-mono">
            {dataset.row_count !== null ? dataset.row_count.toLocaleString() : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Columns</dt>
          <dd className="font-mono">{dataset.columns.length || '—'}</dd>
        </div>
      </dl>
      {!dataset.available && (
        <p className="mt-2 text-xs text-amber-600">No parquet at this URI yet.</p>
      )}
    </button>
  )
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'number') {
    return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(4)
  }
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function compareCellValues(a: unknown, b: unknown): number {
  const an = typeof a === 'number' ? a : typeof a === 'string' && a.trim() ? Number(a) : NaN
  const bn = typeof b === 'number' ? b : typeof b === 'string' && b.trim() ? Number(b) : NaN
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn
  return String(a ?? '').localeCompare(String(b ?? ''))
}
